/**
 * FeedbackCard/index.tsx — 8211-A
 *
 * AI feedback card component with collapsed/expanded states.
 *
 * States:
 * - Collapsed Default: category label + truncated text
 * - Collapsed Hover: same + action icons visible
 * - Expanded Text: full text, resolve + delete in header
 * - Expanded Diff: diff + Apply in footer, resolve + delete in header
 * - Expanded Text+Diff: comment text + diff, Apply in footer
 * - Expanded Multiple Diffs: same as Text+Diff (handled by InlineDiffViewer)
 *
 * Accordion behavior is managed by the parent via isExpanded/onExpand props.
 */
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import IconButton from "../IconButton/index";
import InlineDiffViewer from "../Comment/InlineDiffViewer";
import ReplyBox from "../Comment/ReplyBox";
import Reply from "../Comment/Reply";
import { CommentReplies } from "../Comment/styles";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import {
  CardContainer,
  CardHeaderRow,
  CategoryLabelGroup,
  CategoryLabel,
  CardTextContent,
  HoverActions,
  DiffContainer,
  DiffControlsRow,
  DiffLeftActions,
  DiffApplyButton,
  DiffCopyButton,
  ThumbsRow,
  ThumbButton,
  CollapsedReplyIndicator,
} from "./styles";
import { CommentCardMarkdown } from "./CommentCardMarkdown";
import { DeleteCommentConfirmPortal } from "./DeleteCommentConfirmPortal";
import { CommentAvatar } from "./CommentAvatar";
import { resolveFeedbackHeader } from "src/shared/feedbackCategories";
import { useCommentStore } from "src/stores/commentStore";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { useToast } from "../ToastWrapper";
import { cancelReply, submitReply } from "../Comment/replySubmission";
import { getRepliesForRendering } from "../Comment/sharedReplyVisibility";
import { sendAppEvent } from "src/Utils";

function normalizeDiffPreviewText(value: string | null | undefined): string {
  return String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{2,}/g, "\n");
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeedbackComment {
  id: string;
  annotationId: string;
  aiId: string | null;
  content: string;
  original: string | null;
  suggestion: string | null;
  metadata?: {
    feedbackCategory?: string | null;
    feedbackType?: string | null;
    [key: string]: unknown;
  };
  createdAt: string;
  replies: unknown[];
  [key: string]: unknown;
}

interface FeedbackCardProps {
  comment: FeedbackComment;
  /** Whether this card is currently expanded (accordion) */
  isExpanded: boolean;
  /** Called with comment.id to expand, or null to collapse */
  onExpand: (id: string | null) => void;
  /** Called when clicking an already-expanded card to scroll its annotation into view */
  onScrollToAnnotation?: (commentId: string) => void;
  /** Called when user clicks Apply */
  onApply: (comment: FeedbackComment) => void;
  /** Called when user clicks Dismiss (resolve) */
  onDismiss: (comment: FeedbackComment) => void;
  /** Legacy callback retained for compatibility; no delete action is shown in this card */
  onDelete: (commentId: string, annotationId: string) => void;
  /** Whether to play the wiggle animation (e.g., when opened from chat link) */
  shouldWiggle?: boolean;
  /** Plugin installations for feedback type resolution — pass from parent to avoid per-card fetches */
  installations?: Array<{ name: string; installed: boolean; [key: string]: unknown }>;
  /** Document ID needed for submitting replies */
  documentId?: string | null;
  /** Location label for analytics (e.g. "Editor") */
  commentLocation?: string;
  /** Whether the app is in mobile mode */
  isMobile?: boolean;
  /** Show only delete action in the header (used in resolved/ignored views) */
  showDeleteOnlyAction?: boolean;
  /** Current sidebar filter tab (analytics for delete confirms) */
  commentFilterForAnalytics?: string;
  /** Called when user clicks Undo from resolved/ignored views */
  onUndo?: (comment: FeedbackComment) => void;
  /** Whether undo is currently possible for this card */
  canUndo?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedbackCard({
  comment,
  isExpanded,
  onExpand,
  onScrollToAnnotation,
  onApply,
  onDismiss,
  onDelete,
  shouldWiggle = false,
  installations,
  documentId,
  commentLocation = "Editor",
  isMobile = false,
  showDeleteOnlyAction = false,
  commentFilterForAnalytics,
  onUndo,
  canUndo = false,
}: FeedbackCardProps): JSX.Element {
  const revealAnnotation = useCommentStore((state) => state.revealAnnotation);
  const hoverAnnotation = useCommentStore((state) => state.hoverAnnotation);
  const hoveredAnnotationIds = useCommentStore((state) => state.hoveredAnnotationIds);
  const activeAnnotationIds = useCommentStore((state) => state.activeAnnotationIds);
  const setCommentScrollSyncSuppressedUntil = useCommentStore((state) => state.setCommentScrollSyncSuppressedUntil);
  const submitComment = useCommentStore((state) => state.submitComment);
  const setActiveAnnotationIds = useCommentStore((state) => state.setActiveAnnotationIds);
  const isSharedDocument = useCommentStore((state) => state.isSharedDocument);
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat ?? state.subscription?.hasAccessToAgenticChat ?? false);
  const [isHovered, setIsHovered] = useState(false);
  const replySubmitInFlightRef = useRef(false);
  const [isEditingReply, setIsEditingReply] = useState(false);
  const [newReply, setNewReply] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [thumbVote, setThumbVote] = useState<"up" | "down" | null>(null);
  const { setToast } = useToast();

  // Reveal the gold highlight when the card mounts
  useEffect(() => {
    return revealAnnotation(comment.annotationId);
  }, [comment.annotationId, revealAnnotation]);

  // Apply brighter hover highlight when hovered or expanded
  useEffect(() => {
    if (isHovered || isExpanded || hoveredAnnotationIds?.includes(comment.annotationId) || activeAnnotationIds?.includes(comment.annotationId)) {
      return hoverAnnotation(comment.annotationId);
    }
  }, [isHovered, isExpanded, hoveredAnnotationIds, activeAnnotationIds, comment.annotationId, hoverAnnotation]);

  const hasDiff = comment.original != null && comment.suggestion != null && comment.suggestion !== "";
  const hasContent = comment.content != null && comment.content.trim() !== "";
  const diffOriginal = hasDiff ? normalizeDiffPreviewText(comment.original) : "";
  const diffSuggestion = hasDiff ? normalizeDiffPreviewText(comment.suggestion) : "";
  const visibleReplies = getRepliesForRendering({
    replies: comment.replies,
    isSharedDocument,
  });
  const replyCountLabel = visibleReplies.length === 1 ? "1 reply" : `${visibleReplies.length} replies`;
  const replyPlaceholder = !isSharedDocument && hasAccessToAgenticChat ? "Comment or @ to ask Sudowrite" : "Reply";

  const { headerLabel, headerIcon } = resolveFeedbackHeader(comment.metadata, installations);

  // Click handlers
  const handleCardClick = () => {
    if (!isExpanded) {
      onExpand(comment.id);
    } else if (onScrollToAnnotation) {
      // If already expanded, scroll the annotation back into view
      onScrollToAnnotation(comment.id);
    }
  };

  const handleHeaderClick = (e: React.MouseEvent) => {
    if (isExpanded) {
      e.stopPropagation();
      onExpand(null);
      // Prevent scroll-sync from instantly re-opening the same comment after manual collapse.
      setCommentScrollSyncSuppressedUntil?.(Date.now() + 2000);
    }
  };

  const handleApply = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation();
    onApply(comment);
  };

  const handleResolve = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onApply(comment);
  };

  const handleIgnore = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onDismiss(comment);
  };

  const handleDelete = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (showDeleteOnlyAction) {
      setShowDeleteConfirm(true);
      return;
    }
    onDelete(comment.id, comment.annotationId);
  };

  const handleUndo = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!canUndo || !onUndo) return;
    sendAppEvent("Comment - Undo Clicked", { comment_id: comment.id });
    onUndo(comment);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    sendAppEvent("Comment - Delete Confirmed", {
      comment_id: comment.id,
      filter_type: commentFilterForAnalytics,
    });
    onDelete(comment.id, comment.annotationId);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleCopySuggestion = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (comment.suggestion && navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(comment.suggestion);
    }
  };

  const handleReplySubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    await submitReply({
      newReply,
      isSubmittingReply,
      replySubmitInFlightRef,
      isMobile,
      setActiveAnnotationIds,
      setToast,
      documentId,
      commentLocation,
      submitComment,
      comment,
      setNewReply,
      setIsSubmittingReply,
      onSubmitFailed: (restoreReply, message) => {
        setToast(message);
        setNewReply(restoreReply);
      },
    });
  };

  const handleReplyCancel = () => {
    cancelReply({
      isSubmittingReply,
      setNewReply,
    });
  };

  return (
    <CardContainer
      data-testid="feedback-card"
      $isExpanded={isExpanded}
      $shouldWiggle={shouldWiggle}
      aria-expanded={isExpanded}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header */}
      <CardHeaderRow
        data-testid="feedback-card-header"
        onClick={handleHeaderClick}
      >
        <CategoryLabelGroup>
          <CommentAvatar
            kind={headerLabel === "SUDOWRITE" ? "sudowrite" : "feedback"}
            label={headerLabel}
            iconSrc={headerIcon}
          />
          <CategoryLabel>{headerLabel}</CategoryLabel>
        </CategoryLabelGroup>

        {isExpanded && (
          <HoverActions style={{ opacity: 1 }}>
            {showDeleteOnlyAction ? (
              <>
                {canUndo && (
                  <IconButton
                    data-testid="feedback-undo-btn"
                    src="/assets/formatting-undo.svg"
                    size={12}
                    iconSize={14}
                    hoverTipText="Undo"
                    ariaLabel="Undo"
                    onClick={handleUndo}
                  />
                )}
                <IconButton
                  data-testid="feedback-delete-btn"
                  src="/assets/delete.svg"
                  size={12}
                  hoverTipText="Delete"
                  ariaLabel="Delete"
                  onClick={handleDelete}
                />
              </>
            ) : (
              <>
                <IconButton
                  data-testid="feedback-resolve-btn"
                  src="/assets/check-simple.svg"
                  size={12}
                  hoverTipText="Accept"
                  ariaLabel="Accept"
                  onClick={handleResolve}
                />
                <IconButton
                  data-testid="feedback-ignore-btn"
                  src="/assets/close.svg"
                  size={12}
                  hoverTipText="Ignore"
                  ariaLabel="Ignore"
                  onClick={handleIgnore}
                />
              </>
            )}
          </HoverActions>
        )}
      </CardHeaderRow>

      {/* Text content */}
      {hasContent && (
        <CardTextContent
          data-testid="feedback-card-text"
          $truncate={!isExpanded}
        >
          <CommentCardMarkdown source={comment.content} />
        </CardTextContent>
      )}

      {/* Diff viewer — only when expanded */}
      {isExpanded && hasDiff && (
        <DiffContainer>
          <InlineDiffViewer
            string1={diffOriginal}
            string2={diffSuggestion}
            cleanUp="semantic"
            diffUnit="word"
          />
          <DiffControlsRow>
            <DiffLeftActions>
              <SudoTooltip
                text="Apply"
                position="bottom"
                hasArrow={false}
              >
                <DiffApplyButton
                  role="button"
                  tabIndex={0}
                  aria-label="Apply suggestion"
                  onClick={handleApply}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleApply(e);
                    }
                  }}
                >
                  <SVG
                    src="/assets/arrow.backward.circle.svg"
                    aria-hidden={true}
                    width={22}
                    height={22}
                  />
                </DiffApplyButton>
              </SudoTooltip>
              <SudoTooltip
                text="Copy suggestion"
                position="bottom"
                hasArrow={false}
              >
                <DiffCopyButton
                  role="button"
                  tabIndex={0}
                  aria-label="Copy suggestion"
                  onClick={handleCopySuggestion}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCopySuggestion(e);
                    }
                  }}
                >
                  <SVG
                    src="/assets/copy2.svg"
                    aria-hidden={true}
                    width={22}
                    height={22}
                  />
                </DiffCopyButton>
              </SudoTooltip>
            </DiffLeftActions>
            <ThumbsRow>
              <ThumbButton
                role="button"
                tabIndex={0}
                aria-label="Thumbs up"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setThumbVote(thumbVote === "up" ? null : "up");
                }}
              >
                <SVG
                  src={thumbVote === "up" ? "/assets/thumbsup-filled.svg" : "/assets/thumbsup.svg"}
                  aria-hidden={true}
                />
              </ThumbButton>
              <ThumbButton
                role="button"
                tabIndex={0}
                aria-label="Thumbs down"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setThumbVote(thumbVote === "down" ? null : "down");
                }}
              >
                <SVG
                  src={thumbVote === "down" ? "/assets/thumbsdown-filled.svg" : "/assets/thumbsdown.svg"}
                  aria-hidden={true}
                />
              </ThumbButton>
            </ThumbsRow>
          </DiffControlsRow>
        </DiffContainer>
      )}

      {isExpanded && visibleReplies.length > 0 && (
        <CommentReplies data-testid="feedback-card-replies">
          {visibleReplies.map((reply: any) => (
            <Reply
              key={reply.id}
              reply={reply}
              setIsEditingReply={setIsEditingReply}
              isMobile={isMobile}
              forceOpen={isExpanded}
            />
          ))}
        </CommentReplies>
      )}

      {!isExpanded && visibleReplies.length > 0 && (
        <CollapsedReplyIndicator data-testid="feedback-card-reply-indicator">
          {replyCountLabel}
        </CollapsedReplyIndicator>
      )}

      {isExpanded && !showDeleteOnlyAction && (isMobile || !isEditingReply) && (
        <ReplyBox
          isMobile={isMobile}
          cancelComment={handleReplyCancel}
          submitComment={handleReplySubmit}
          newComment={newReply}
          setNewComment={setNewReply}
          newCommentInputBoxRef={null}
          placeholder={replyPlaceholder}
          buttonText="Reply"
          alwaysShowActionButtons={isMobile}
          isSubmitting={isSubmittingReply}
        />
      )}
      <DeleteCommentConfirmPortal
        open={showDeleteConfirm}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      {isExpanded && showDeleteOnlyAction && !canUndo && (
        <div style={{ fontSize: 12, color: "var(--grey-2)", lineHeight: "16px", width: "100%" }}>We can't undo this comment because the document has changed.</div>
      )}
    </CardContainer>
  );
}

export default FeedbackCard;
